import { Link } from "../shared/SiteLink";
import { Phone, Mail, MapPin, Clock, MessageCircle, Monitor } from "lucide-react";
import { EditableText } from "../shared/EditableText";
import { useContentEditingSafe } from "../shared/ContentEditingContext";
import { useTemplateType } from "../shared/TemplateContext";
import { useCustomNav } from "../shared/useCustomNav";
import { useVisibleCategories } from "../shared/useVisibleCategories";
import {
  identity as brandingIdentity,
  footerSolutionLinks as brandingSolutionLinks,
} from "../../siteConfig";
import {
  identity as storeIdentity,
} from "../../storeSiteConfig";
import { useBusinessInfo } from "../shared/useBusinessInfo";

/**
 * In edit mode renders a <span>, otherwise a <Link>.
 * External URLs (http/https) render as <a> tags.
 */
function NavLink({
  to,
  className,
  isEditMode,
  children,
}: {
  to: string;
  className?: string;
  isEditMode: boolean;
  children: React.ReactNode;
}) {
  if (isEditMode) {
    return <span className={className}>{children}</span>;
  }
  if (to.startsWith("http://") || to.startsWith("https://")) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

/**
 * In edit mode renders a <span>, otherwise an <a>.
 */
function ExtLink({
  href,
  className,
  isEditMode,
  target,
  rel,
  children,
}: {
  href: string;
  className?: string;
  isEditMode: boolean;
  target?: string;
  rel?: string;
  children: React.ReactNode;
}) {
  if (isEditMode) {
    return <span className={className}>{children}</span>;
  }
  return (
    <a href={href} className={className} target={target} rel={rel}>
      {children}
    </a>
  );
}

export function Footer() {
  const ctx = useContentEditingSafe();
  const isEditMode = ctx?.isEditMode ?? false;
  const templateType = useTemplateType();
  const isStore = templateType === "store";
  const { visibleNav } = useCustomNav();
  const visibleCategories = useVisibleCategories();

  const identity = isStore ? storeIdentity : brandingIdentity;
  const businessInfo = useBusinessInfo();

  // Footer quick links = visible nav items (merged, minus hidden)
  const quickLinks = visibleNav.map((n) => ({ label: n.label, path: n.path }));

  // Second column links: for store templates show categories, for branding show solutions
  const secondColTitle = isStore
    ? "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA"
    : "\u05E4\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA";
  const secondColLinks = isStore
    ? visibleCategories.map((c) => ({ label: c.name, path: `/catalog/${c.slug}` }))
    : brandingSolutionLinks;

  return (
    <footer className="bg-zap-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-zap rounded-full flex items-center justify-center">
                <Monitor className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col">
                <EditableText
                  contentKey="footer:brand-name"
                  defaultValue={identity.name}
                  as="span"
                  className="text-lg leading-tight"
                  style={{ fontWeight: 700 }}
                  hideTagBadge
                />
                <EditableText
                  contentKey="footer:brand-name-heb"
                  defaultValue={identity.nameHebrew}
                  as="span"
                  className="text-white/40 text-[10px] leading-tight"
                  hideTagBadge
                />
              </div>
            </div>
            <EditableText
              contentKey="footer:brand-description"
              defaultValue={identity.description}
              as="p"
              className="text-white/60 text-sm leading-relaxed mb-5"
              hideTagBadge
            />
          </div>

          {/* Quick Links */}
          <div>
            <EditableText
              contentKey="footer:quick-links-title"
              defaultValue={"\u05E7\u05D9\u05E9\u05D5\u05E8\u05D9\u05DD \u05DE\u05D4\u05D9\u05E8\u05D9\u05DD"}
              as="h3"
              className="text-base mb-4"
              style={{ fontWeight: 600 }}
              hideTagBadge
            />
            <ul className="space-y-2 text-sm">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    isEditMode={isEditMode}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <EditableText
                      contentKey={`nav:${link.path}`}
                      defaultValue={link.label}
                      as="span"
                      hideTagBadge
                    />
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Second Column — Solutions or Categories */}
          <div>
            <EditableText
              contentKey="footer:solutions-title"
              defaultValue={secondColTitle}
              as="h3"
              className="text-base mb-4"
              style={{ fontWeight: 600 }}
              hideTagBadge
            />
            <ul className="space-y-2 text-sm">
              {secondColLinks.map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    isEditMode={isEditMode}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <EditableText
                      contentKey={`nav:${link.path}`}
                      defaultValue={link.label}
                      as="span"
                      hideTagBadge
                    />
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <EditableText
              contentKey="footer:contact-title"
              defaultValue={"\u05D9\u05E6\u05D9\u05E8\u05EA \u05E7\u05E9\u05E8"}
              as="h3"
              className="text-base mb-4"
              style={{ fontWeight: 600 }}
              hideTagBadge
            />
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 text-white/60">
                <Phone className="w-4 h-4 shrink-0" />
                <ExtLink href={businessInfo.phoneHref} isEditMode={isEditMode} className="hover:text-white transition-colors">
                  <EditableText contentKey="biz:contact:seo-phone" defaultValue={businessInfo.phone} as="span" hideTagBadge />
                </ExtLink>
              </li>
              <li className="flex items-center gap-2 text-white/60">
                <MessageCircle className="w-4 h-4 shrink-0" />
                <ExtLink href={businessInfo.whatsappUrl} isEditMode={isEditMode} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  <EditableText contentKey="biz:contact:whatsapp" defaultValue={businessInfo.mobile} as="span" hideTagBadge />
                </ExtLink>
              </li>
              <li className="flex items-center gap-2 text-white/60">
                <Mail className="w-4 h-4 shrink-0" />
                <ExtLink href={businessInfo.emailHref} isEditMode={isEditMode} className="hover:text-white transition-colors">
                  <EditableText contentKey="biz:contact:email" defaultValue={businessInfo.email} as="span" hideTagBadge />
                </ExtLink>
              </li>
              <li className="flex items-start gap-2 text-white/60">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                <EditableText contentKey="biz:contact:address" defaultValue={businessInfo.address} as="span" hideTagBadge />
              </li>
              <li className="flex items-start gap-2 text-white/60">
                <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                <EditableText contentKey="biz:contact:hours" defaultValue={businessInfo.hoursStr || businessInfo.hours.map((h) => `${h.days} ${h.time}`).join(", ")} as="div" hideTagBadge />
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar — copyright only (admin links moved to AdminBar at top) */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-white/40">
          <EditableText contentKey="footer:copyright" defaultValue={identity.copyright} as="span" hideTagBadge />
          <EditableText contentKey="footer:rights" defaultValue={identity.rightsReserved} as="span" hideTagBadge />
        </div>
      </div>
    </footer>
  );
}